import { useRef, useState } from "react";
import { motion } from "framer-motion";
import { AlertCircle, Check, Upload, X } from "lucide-react";
import { toast } from "sonner";
import SoftButton from "@/components/SoftButton";
import { MOSAIC_TYPES, MOSAIC_TYPE_ORDER } from "@/lib/mosaicData";
import { MOSAIC } from "@/constants/testIds";

const MAX_PHOTO_BYTES = 900 * 1024;
const REFERENCE_TYPES = new Set(["song", "movie", "book", "place", "person", "experience", "project"]);

const FIELD_COPY = {
  song: { title: "Song title", detail: "Artist (optional)", url: "Link (optional)", note: "Why this one (optional)" },
  movie: { title: "Film title", detail: "Director, year, or where you saw it (optional)", url: "Link (optional)", note: "What stayed with you (optional)" },
  book: { title: "Book title", detail: "Author (optional)", url: "Link (optional)", note: "What this opened up for you (optional)" },
  place: { title: "Place", detail: "City, trail, room, coastline… (optional)", url: "Map or reference link (optional)", note: "Why this place matters (optional)" },
  person: { title: "Person", detail: "How you know them (optional)", url: "Link or reference (optional)", note: "What you want to remember about them (optional)" },
  experience: { title: "Experience", detail: "When or where (optional)", url: "Link or reference (optional)", note: "Why this belongs in your Mosaic (optional)" },
  project: { title: "Project", detail: "What it is (optional)", url: "Link or reference (optional)", note: "Why you are building it or why it matters (optional)" },
};

export default function MosaicComposer({ onSave, onCancel }) {
  const [type, setType] = useState("quote");
  const fileInputRef = useRef(null);
  const [text, setText] = useState("");
  const [source, setSource] = useState("");
  const [from, setFrom] = useState("");
  const [title, setTitle] = useState("");
  const [detail, setDetail] = useState("");
  const [url, setUrl] = useState("");
  const [note, setNote] = useState("");
  const [caption, setCaption] = useState("");
  const [dataUrl, setDataUrl] = useState("");
  const [photoSizeWarn, setPhotoSizeWarn] = useState(false);

  const resetAll = () => {
    setText("");
    setSource("");
    setFrom("");
    setTitle("");
    setDetail("");
    setUrl("");
    setNote("");
    setCaption("");
    setDataUrl("");
    setPhotoSizeWarn(false);
  };

  const handleTypeChange = (newType) => {
    setType(newType);
    resetAll();
  };

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("That doesn't look like an image.");
      return;
    }
    setPhotoSizeWarn(file.size > MAX_PHOTO_BYTES);
    const reader = new FileReader();
    reader.onload = (ev) => setDataUrl(String(ev.target?.result || ""));
    reader.onerror = () => toast.error("Could not read that image.");
    reader.readAsDataURL(file);
  };

  const canSave = () => {
    if (["quote", "reminder", "message", "memory"].includes(type)) return text.trim().length > 0;
    if (REFERENCE_TYPES.has(type)) return title.trim().length > 0;
    if (type === "photo") return dataUrl.length > 0;
    return false;
  };

  const handleSave = () => {
    if (!canSave()) return;
    let payload;
    if (type === "quote") payload = { type, text: text.trim(), source: source.trim() || null };
    else if (type === "reminder" || type === "memory") payload = { type, text: text.trim() };
    else if (type === "message") payload = { type, text: text.trim(), from: from.trim() || null };
    else if (REFERENCE_TYPES.has(type)) {
      payload = {
        type,
        title: title.trim(),
        detail: detail.trim() || null,
        url: url.trim() || null,
        note: note.trim() || null,
      };
      if (type === "song") payload.artist = payload.detail;
    } else if (type === "photo") payload = { type, dataUrl, caption: caption.trim() || null };
    onSave(payload);
    resetAll();
  };

  const referenceCopy = FIELD_COPY[type];

  return (
    <motion.div
      initial={{ opacity: 0, y: 8, height: 0 }}
      animate={{ opacity: 1, y: 0, height: "auto" }}
      exit={{ opacity: 0, y: 8, height: 0 }}
      transition={{ duration: 0.3 }}
      className="un-card p-5 mb-7 overflow-hidden"
    >
      <p className="text-[11px] leading-relaxed mb-3" style={{ color: "var(--un-text-3)" }}>
        Keep a piece because it means something to you — not because it needs to impress anyone else.
      </p>

      <div className="flex gap-2 overflow-x-auto pb-2 mb-4 -mx-1 px-1">
        {MOSAIC_TYPE_ORDER.map((tKey) => {
          const t = MOSAIC_TYPES[tKey];
          const active = type === tKey;
          return (
            <button
              key={tKey}
              data-testid={`${MOSAIC.typeSelectorPrefix}${tKey}`}
              onClick={() => handleTypeChange(tKey)}
              className="shrink-0 px-3 py-1.5 rounded-full text-xs font-medium un-press transition-all"
              style={{
                background: active ? t.accent : "rgba(74,32,64,0.05)",
                color: active ? "#FDFBF7" : "var(--un-text-2)",
                fontFamily: "Manrope, sans-serif",
              }}
            >
              {t.label}
            </button>
          );
        })}
      </div>

      {type === "quote" && (
        <>
          <textarea
            data-testid={MOSAIC.composerText}
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={MOSAIC_TYPES.quote.promptPlaceholder}
            rows={4}
            className="w-full resize-none outline-none rounded-2xl p-4 text-[15px] leading-relaxed mb-2"
            style={{ background: "var(--un-bg-2)", color: "var(--un-text-1)" }}
            autoFocus
          />
          <input
            data-testid={MOSAIC.composerSource}
            value={source}
            onChange={(e) => setSource(e.target.value)}
            placeholder="Source (optional)"
            className="w-full outline-none rounded-2xl p-3 text-sm"
            style={{ background: "var(--un-bg-2)", color: "var(--un-text-1)" }}
          />
        </>
      )}

      {(type === "reminder" || type === "memory") && (
        <textarea
          data-testid={MOSAIC.composerText}
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={MOSAIC_TYPES[type].promptPlaceholder}
          rows={4}
          className="w-full resize-none outline-none rounded-2xl p-4 text-[15px] leading-relaxed"
          style={{ background: "var(--un-bg-2)", color: "var(--un-text-1)" }}
          autoFocus
        />
      )}

      {type === "message" && (
        <>
          <textarea
            data-testid={MOSAIC.composerText}
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={MOSAIC_TYPES.message.promptPlaceholder}
            rows={4}
            className="w-full resize-none outline-none rounded-2xl p-4 text-[15px] leading-relaxed mb-2"
            style={{ background: "var(--un-bg-2)", color: "var(--un-text-1)" }}
            autoFocus
          />
          <input
            data-testid={MOSAIC.composerMessageFrom}
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            placeholder="From (optional)"
            className="w-full outline-none rounded-2xl p-3 text-sm"
            style={{ background: "var(--un-bg-2)", color: "var(--un-text-1)" }}
          />
        </>
      )}

      {REFERENCE_TYPES.has(type) && (
        <div className="space-y-2">
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={referenceCopy.title}
            className="w-full outline-none rounded-2xl p-3 text-[15px]"
            style={{ background: "var(--un-bg-2)", color: "var(--un-text-1)" }}
            autoFocus
          />
          <input
            value={detail}
            onChange={(e) => setDetail(e.target.value)}
            placeholder={referenceCopy.detail}
            className="w-full outline-none rounded-2xl p-3 text-sm"
            style={{ background: "var(--un-bg-2)", color: "var(--un-text-1)" }}
          />
          <input
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder={referenceCopy.url}
            className="w-full outline-none rounded-2xl p-3 text-sm"
            style={{ background: "var(--un-bg-2)", color: "var(--un-text-1)" }}
          />
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder={referenceCopy.note}
            rows={2}
            className="w-full resize-none outline-none rounded-2xl p-3 text-sm"
            style={{ background: "var(--un-bg-2)", color: "var(--un-text-1)" }}
          />
        </div>
      )}

      {type === "photo" && (
        <div>
          <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFile} className="hidden" data-testid={MOSAIC.composerPhoto} />
          {dataUrl ? (
            <div className="overflow-hidden rounded-2xl mb-3" style={{ boxShadow: "0 6px 18px rgba(74,32,64,0.08)" }}>
              <img src={dataUrl} alt="preview" className="w-full block" />
            </div>
          ) : (
            <button
              onClick={() => fileInputRef.current?.click()}
              className="w-full rounded-2xl py-8 flex flex-col items-center gap-2 un-press"
              style={{ background: "var(--un-bg-2)", border: "1.5px dashed rgba(74,32,64,0.18)" }}
            >
              <Upload size={18} style={{ color: "var(--un-plum)" }} />
              <span className="text-sm font-medium" style={{ color: "var(--un-text-2)" }}>Choose a photo</span>
              <span className="text-[11px]" style={{ color: "var(--un-text-3)" }}>Stays on this device.</span>
            </button>
          )}
          {photoSizeWarn && (
            <div className="flex items-start gap-2 text-[12px] mt-1 mb-2 px-2" style={{ color: "var(--un-burgundy)" }}>
              <AlertCircle size={12} className="mt-0.5" />
              <span>That photo is larger than ~900KB. It will still save, but a smaller image keeps things light.</span>
            </div>
          )}
          {dataUrl && (
            <input
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              placeholder="Caption (optional)"
              className="w-full outline-none rounded-2xl p-3 text-sm"
              style={{ background: "var(--un-bg-2)", color: "var(--un-text-1)" }}
            />
          )}
        </div>
      )}

      <div className="flex items-center justify-end gap-2 mt-4">
        <SoftButton data-testid={MOSAIC.composerCancel} variant="ghost" size="sm" onClick={onCancel} iconLeft={<X size={14} />}>
          Cancel
        </SoftButton>
        <SoftButton data-testid={MOSAIC.composerSave} variant="primary" size="sm" onClick={handleSave} disabled={!canSave()} iconLeft={<Check size={14} />}>
          Keep this piece
        </SoftButton>
      </div>
    </motion.div>
  );
}
